import { Restore, Edit, ExpandMore } from '@mui/icons-material';
// Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormLabel,
  IconButton,
  Typography
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { FC } from 'react';
import { isAllowedToBeEditedOrDeleted } from '../../../helpers';
// Types
import { IDomainUser } from '../../../models';
import { useFlags } from '../../../hooks/useFlags';

interface IMobileAdminUser {
  original: IDomainUser;
  handleResetAuth?: (val: IDomainUser) => void;
  handleEdit?: (val: IDomainUser) => void;
}

export const MobileAdminUser: FC<IMobileAdminUser> = ({
  original,
  handleResetAuth,
  handleEdit
}) => {
  const classes = useStyles();
  const featureFlags = useFlags();

  const { displayName, accountName, placeOrderRole, reportsTo, email } = original;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">{displayName}</Typography>
          <div className={classes.buttonsWrapper}>
            {handleEdit && (
              <IconButton
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  handleEdit(original);
                }}
                sx={{ padding: '0 5px' }}
                size="large"
              >
                <Edit />
              </IconButton>
            )}
            {handleResetAuth && (
              <IconButton
                color="secondary"
                onClick={e => {
                  e.stopPropagation();
                  handleResetAuth(original);
                }}
                sx={{ padding: '0 5px' }}
                size="large"
              >
                <Restore />
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Name:
          </FormLabel>
          <Typography component="span" variant="body2">
            {displayName || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Role:
          </FormLabel>
          <Typography component="span" variant="body2">
            {placeOrderRole?.name ?? '--'}
          </Typography>
        </div>
        {isAllowedToBeEditedOrDeleted(placeOrderRole?.name) && (
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Reports To:
            </FormLabel>
            <Typography component="span" variant="body2">
              {reportsTo?.displayName ?? '--'}
            </Typography>
          </div>
        )}
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Email:
          </FormLabel>
          <Typography component="span" variant="body2">
            {featureFlags?.entraId
              ? email
                ? email
                : '--'
              : accountName
              ? `${accountName}@stonemartin.com`
              : '--'}
          </Typography>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  roleWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: {
    padding: `2px 5px`,
    color: theme.palette.error.main
  },
  boldName: {
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    color: theme.palette.typography.labels,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  }
}));
